import { uiText, useUiLanguage } from '@/i18n/interface';
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormData } from "../../utils/formValidation";

interface MartialArtsBackgroundStepProps {
  formData: FormData;
  updateFormData: (field: string, value: string) => void;
}

const MartialArtsBackgroundStep: React.FC<MartialArtsBackgroundStepProps> = ({
  formData,
  updateFormData,
}) => {
  useUiLanguage();
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{uiText("Martial Arts Background & Goals")}</h3>
      <div>
        <Label>{uiText("Previous Martial Arts Experience *")}</Label>
        <RadioGroup
          value={formData.previousExperience}
          onValueChange={(value) => updateFormData("previousExperience", value)}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="none" id="none" />
            <Label htmlFor="none">{uiText("No previous experience")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="beginner" id="beginner" />
            <Label htmlFor="beginner">{uiText("Beginner (less than 1 year)")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="intermediate" id="intermediate" />
            <Label htmlFor="intermediate">{uiText("Intermediate (1-3 years)")}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="advanced" id="advanced" />
            <Label htmlFor="advanced">{uiText("Advanced (3+ years)")}</Label>
          </div>
        </RadioGroup>
      </div>

      {formData.previousExperience !== "none" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="previousStyles">{uiText("Previous Martial Arts Styles")}</Label>
            <Input
              id="previousStyles"
              value={formData.previousStyles}
              onChange={(e) => updateFormData("previousStyles", e.target.value)}
              placeholder={uiText("e.g., Karate, Taekwondo, Judo")}
            />
          </div>
          <div>
            <Label htmlFor="previousBelt">{uiText("Highest Belt/Rank Achieved")}</Label>
            <Input
              id="previousBelt"
              value={formData.previousBelt}
              onChange={(e) => updateFormData("previousBelt", e.target.value)}
              placeholder={uiText("e.g., Yellow Belt, 2nd Kyu")}
            />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="trainingGoals">{uiText("Training Goals *")}</Label>
        <Textarea
          id="trainingGoals"
          value={formData.trainingGoals}
          onChange={(e) => updateFormData("trainingGoals", e.target.value)}
          placeholder={uiText("What do you hope to achieve through Kyokushin Karate training?")}
          required
        />
      </div>

      <div>
        <Label htmlFor="howDidYouHear">
          {uiText("How did you hear about WarriorHub?")}</Label>
        <Select
          onValueChange={(value) => updateFormData("howDidYouHear", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={uiText("Select an option")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="website">{uiText("Website")}</SelectItem>
            <SelectItem value="social-media">{uiText("Social Media")}</SelectItem>
            <SelectItem value="friend">{uiText("Friend/Family")}</SelectItem>
            <SelectItem value="search">{uiText("Search Engine")}</SelectItem>
            <SelectItem value="flyer">{uiText("Flyer/Advertisement")}</SelectItem>
            <SelectItem value="other">{uiText("Other")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default MartialArtsBackgroundStep;
